/** SUNTRONIX ONE — ONE STUDIO (no-code configuration).
 *  Administration → ONE STUDIO. Three pillars:
 *   • Custom Fields  — per-module custom field designer (config-studio/fields)
 *   • Status Designer — per-module status sets with colour (config-studio picklists, key status.<module>)
 *   • Rule Builder    — deep-links to the existing Rule & Process studios
 *  Admin-gated (super_admin). Everything reuses the governed config-studio API. */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Sliders, ListChecks, GitBranch, Plus, Trash2, Save, ArrowUpRight,
  GripVertical, Loader2, ShieldCheck, SlidersHorizontal,
} from "lucide-react";
import EmphasisProfiles from "@/components/technology/EmphasisProfiles";

const PILLARS = [
  { id: "fields", label: "Custom Fields", icon: Sliders,
    blurb: "Add your own fields to any module — text, dates, currency, dropdowns and more." },
  { id: "status", label: "Status Designer", icon: ListChecks,
    blurb: "Design the status stages each module moves through, with colour and order." },
  { id: "rules", label: "Rule Builder", icon: GitBranch,
    blurb: "Automate the platform with condition → action rules and approval flows." },
  { id: "emphasis", label: "Emphasis Profiles", icon: SlidersHorizontal,
    blurb: "Choose which product spec types Smart Format bolds, per brand." },
];

// Modules that carry a lifecycle status set.
const STATUS_MODULES = [
  ["quotations", "Quotations"], ["customer_po", "Customer PO"], ["orders", "Orders"],
  ["procurement", "Procurement"], ["assets", "Assets"], ["deployments", "Deployments"],
  ["recovery", "Recovery"], ["support", "Support"],
];

const STATUS_COLOURS = [
  { v: "slate", c: "#64748b" }, { v: "sky", c: "#0ea5e9" }, { v: "indigo", c: "#6366f1" },
  { v: "emerald", c: "#10b981" }, { v: "amber", c: "#f59e0b" }, { v: "rose", c: "#f43f5e" },
  { v: "violet", c: "#8b5cf6" },
];
const colourHex = (v) => (STATUS_COLOURS.find((x) => x.v === v) || {}).c || "#64748b";

export default function OneStudio() {
  const [tab, setTab] = useState("fields");
  const [meta, setMeta] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/config-studio/meta").then((r) => setMeta(r.data)).catch(() => setMeta({}));
  }, []);

  const canAdmin = !!meta?.current_user?.can_admin;

  return (
    <div className="space-y-5" data-testid="one-studio">
      <div>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-indigo-600 font-semibold">
          <ShieldCheck className="h-3.5 w-3.5" /> ONE STUDIO
        </div>
        <h1 className="text-xl font-heading font-semibold text-slate-900 mt-1">No-code configuration</h1>
        <p className="text-[13px] text-slate-500 mt-0.5 max-w-2xl">
          Shape SUNTRONIX ONE without code — add fields, design statuses and automate with rules.
          {!canAdmin && " You have read-only access; changes require a super admin."}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {PILLARS.map((p) => {
          const active = tab === p.id;
          return (
            <button key={p.id} onClick={() => setTab(p.id)}
              data-testid={`studio-pillar-${p.id}`}
              className={"text-left rounded-xl border p-4 transition-all " +
                (active ? "border-indigo-400 bg-indigo-50/60 shadow-sm"
                        : "border-slate-200 bg-white hover:border-slate-300")}>
              <div className="flex items-center gap-2">
                <span className={"h-8 w-8 rounded-lg flex items-center justify-center " +
                  (active ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600")}>
                  <p.icon className="h-4 w-4" />
                </span>
                <span className="font-medium text-slate-900 text-[14px]">{p.label}</span>
              </div>
              <p className="text-[12px] text-slate-500 mt-2">{p.blurb}</p>
            </button>
          );
        })}
      </div>

      <div className="border-t border-slate-100 pt-4">
        {tab === "fields" && <CustomFields meta={meta} canAdmin={canAdmin} />}
        {tab === "status" && <StatusDesigner canAdmin={canAdmin} />}
        {tab === "rules" && <RuleBuilder nav={nav} />}
        {tab === "emphasis" && <EmphasisProfiles />}
      </div>
    </div>
  );
}

/* ── Custom Fields ────────────────────────────────────────────────── */
const BLANK_FIELD = {
  field_key: "", label: "", field_type: "text", required: false,
  help_text: "", order: 0, active: true, picklist_key: "",
};

function CustomFields({ meta, canAdmin }) {
  const [modules, setModules] = useState([]);
  const [module, setModule] = useState("quotations");
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(BLANK_FIELD);
  const [busy, setBusy] = useState(false);
  const fieldTypes = meta?.field_types || ["text"];

  useEffect(() => {
    api.get("/config-studio/modules")
      .then((r) => setModules(r.data.items || [])).catch(() => {});
  }, []);

  const load = useCallback(() => api.get(`/config-studio/fields/${module}`)
    .then((r) => setRows(r.data.items || [])).catch(() => setRows([])), [module]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [load, module]);

  const save = async () => {
    const key = form.field_key.trim().toLowerCase().replace(/\s+/g, "_");
    if (!key || !form.label.trim()) return toast.error("Field key and label are required");
    setBusy(true);
    try {
      await api.put("/config-studio/fields", {
        module, field_key: key, label: form.label.trim(), field_type: form.field_type,
        required: form.required, help_text: form.help_text || null,
        order: Number(form.order) || rows.length,
        picklist_key: (form.field_type.includes("select") ? form.picklist_key || null : null),
        active: form.active,
      });
      toast.success(`Field "${form.label}" saved`);
      setForm(BLANK_FIELD); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  const del = async (fk) => {
    if (!window.confirm("Delete this custom field?")) return;
    try {
      await api.delete(`/config-studio/fields/${module}/${fk}`);
      toast.success("Field deleted"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="studio-fields">
      <div className="flex items-center gap-2">
        <span className="text-[11px] uppercase tracking-wider text-slate-500">Module</span>
        <select value={module} onChange={(e) => setModule(e.target.value)}
          data-testid="studio-fields-module"
          className="h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white">
          {(modules.length ? modules.map((m) => [m.module_key, m.display_name]) : [["quotations", "Quotations"]])
            .map(([k, n]) => <option key={k} value={k}>{n}</option>)}
        </select>
        <span className="text-[12px] text-slate-400">{rows.length} custom field(s)</span>
      </div>

      {canAdmin && (
        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50" data-testid="studio-field-form">
          <div className="text-[11px] font-semibold text-slate-900 mb-2">Add / update field</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[12px]">
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="Label (e.g. Project Code)" data-testid="studio-field-label"
              className="border border-slate-200 rounded px-2 py-1.5" />
            <input value={form.field_key} onChange={(e) => setForm({ ...form, field_key: e.target.value })}
              placeholder="field_key (e.g. project_code)" data-testid="studio-field-key"
              className="border border-slate-200 rounded px-2 py-1.5 font-mono" />
            <select value={form.field_type} onChange={(e) => setForm({ ...form, field_type: e.target.value })}
              data-testid="studio-field-type"
              className="border border-slate-200 rounded px-2 py-1.5 bg-white">
              {fieldTypes.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </select>
            {form.field_type.includes("select") && (
              <input value={form.picklist_key} onChange={(e) => setForm({ ...form, picklist_key: e.target.value })}
                placeholder="picklist_key for options" data-testid="studio-field-picklist"
                className="border border-slate-200 rounded px-2 py-1.5 font-mono" />
            )}
            <input value={form.help_text} onChange={(e) => setForm({ ...form, help_text: e.target.value })}
              placeholder="Help text (optional)"
              className="border border-slate-200 rounded px-2 py-1.5 md:col-span-2" />
          </div>
          <div className="flex items-center justify-between mt-2">
            <label className="text-[12px] text-slate-600 flex items-center gap-1.5">
              <input type="checkbox" checked={form.required} data-testid="studio-field-required"
                onChange={(e) => setForm({ ...form, required: e.target.checked })} /> Required
            </label>
            <button onClick={save} disabled={busy} data-testid="studio-field-save"
              className="text-[12px] px-3 py-1.5 rounded bg-slate-900 text-white inline-flex items-center gap-1 disabled:opacity-50">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save field
            </button>
          </div>
        </div>
      )}

      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Label</th>
              <th className="text-left px-3 py-2">Key</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-center px-3 py-2">Required</th>
              <th className="text-center px-3 py-2">Active</th>
              {canAdmin && <th className="px-3 py-2" />}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-slate-400">No custom fields for this module yet.</td></tr>
            )}
            {rows.map((f) => (
              <tr key={f.field_key} className="border-t border-slate-100" data-testid={`studio-field-row-${f.field_key}`}>
                <td className="px-3 py-2 font-medium text-slate-900">{f.label}</td>
                <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{f.field_key}</td>
                <td className="px-3 py-2 text-slate-600">{(f.field_type || "").replace(/_/g, " ")}</td>
                <td className="px-3 py-2 text-center">{f.required ? "Yes" : "—"}</td>
                <td className="px-3 py-2 text-center">
                  <span className={"text-[9px] uppercase " + (f.active !== false ? "text-emerald-600" : "text-rose-500")}>
                    {f.active !== false ? "Active" : "Inactive"}
                  </span>
                </td>
                {canAdmin && (
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => del(f.field_key)} data-testid={`studio-field-del-${f.field_key}`}
                      className="text-rose-500 hover:text-rose-700"><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Status Designer ──────────────────────────────────────────────── */
function StatusDesigner({ canAdmin }) {
  const [module, setModule] = useState("quotations");
  const [values, setValues] = useState([]);
  const [busy, setBusy] = useState(false);
  const key = `status.${module}`;
  const label = useMemo(() => (STATUS_MODULES.find((m) => m[0] === module) || [module, module])[1] + " statuses", [module]);

  const load = useCallback(() => api.get(`/config-studio/picklists/${key}`)
    .then((r) => setValues(r.data.values || []))
    .catch(() => setValues([])), [key]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [load, module]);

  const addRow = () => setValues((s) => [...s, { value: "", label: "", colour: "slate", active: true, sort: s.length }]);
  const upd = (i, k, v) => setValues((s) => s.map((r, ix) => ix === i ? { ...r, [k]: v } : r));
  const rm = (i) => setValues((s) => s.filter((_, ix) => ix !== i));
  const move = (i, dir) => setValues((s) => {
    const j = i + dir; if (j < 0 || j >= s.length) return s;
    const c = [...s]; [c[i], c[j]] = [c[j], c[i]]; return c;
  });

  const save = async () => {
    const clean = values
      .map((v, i) => ({
        value: (v.value || v.label || "").trim().toUpperCase().replace(/\s+/g, "_"),
        label: (v.label || v.value || "").trim(), colour: v.colour || "slate",
        active: v.active !== false, sort: i,
      }))
      .filter((v) => v.value);
    if (!clean.length) return toast.error("Add at least one status");
    setBusy(true);
    try {
      await api.put("/config-studio/picklists", { picklist_key: key, label, values: clean });
      toast.success(`${label} saved`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="studio-status">
      <div className="flex items-center gap-2">
        <span className="text-[11px] uppercase tracking-wider text-slate-500">Module</span>
        <select value={module} onChange={(e) => setModule(e.target.value)}
          data-testid="studio-status-module"
          className="h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white">
          {STATUS_MODULES.map(([k, n]) => <option key={k} value={k}>{n}</option>)}
        </select>
        <span className="text-[11px] font-mono text-slate-400">{key}</span>
      </div>

      <div className="border border-slate-200 rounded-lg divide-y">
        {values.length === 0 && (
          <div className="text-center py-8 text-slate-400 text-[12px]">
            No statuses defined for this module. {canAdmin && "Add the first stage below."}
          </div>
        )}
        {values.map((v, i) => (
          <div key={i} className="flex items-center gap-2 px-3 py-2" data-testid={`studio-status-row-${i}`}>
            {canAdmin && (
              <div className="flex flex-col">
                <button onClick={() => move(i, -1)} className="text-slate-300 hover:text-slate-600 leading-none">▲</button>
                <button onClick={() => move(i, 1)} className="text-slate-300 hover:text-slate-600 leading-none">▼</button>
              </div>
            )}
            <GripVertical className="h-3.5 w-3.5 text-slate-300 shrink-0" />
            <span className="h-4 w-4 rounded-full border border-white shadow-sm shrink-0"
              style={{ background: colourHex(v.colour) }} />
            <input value={v.label} disabled={!canAdmin}
              onChange={(e) => upd(i, "label", e.target.value)}
              placeholder="Status name (e.g. Awaiting Approval)"
              data-testid={`studio-status-label-${i}`}
              className="flex-1 border border-slate-200 rounded px-2 py-1 text-[13px]" />
            <select value={v.colour || "slate"} disabled={!canAdmin}
              onChange={(e) => upd(i, "colour", e.target.value)}
              data-testid={`studio-status-colour-${i}`}
              className="h-8 border border-slate-200 rounded px-1.5 text-[12px] bg-white">
              {STATUS_COLOURS.map((c) => <option key={c.v} value={c.v}>{c.v}</option>)}
            </select>
            <label className="text-[11px] text-slate-500 flex items-center gap-1">
              <input type="checkbox" checked={v.active !== false} disabled={!canAdmin}
                onChange={(e) => upd(i, "active", e.target.checked)} /> On
            </label>
            {canAdmin && (
              <button onClick={() => rm(i)} data-testid={`studio-status-del-${i}`}
                className="text-rose-500 hover:text-rose-700"><Trash2 className="h-3.5 w-3.5" /></button>
            )}
          </div>
        ))}
      </div>

      {canAdmin && (
        <div className="flex items-center justify-between">
          <Button variant="create" size="sm" onClick={addRow} data-testid="studio-status-add">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add status
          </Button>
          <Button size="sm" onClick={save} disabled={busy} data-testid="studio-status-save"
            className="bg-slate-900 hover:bg-slate-800">
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />} Save statuses
          </Button>
        </div>
      )}
    </div>
  );
}

/* ── Rule Builder (links to existing studios) ─────────────────────── */
function RuleBuilder({ nav }) {
  const links = [
    ["Rule Studio", "Condition → action automation rules across modules.", "/administration/rules"],
    ["Process Studio", "Multi-step workflows, stages and approval routing.", "/administration/process-studio"],
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="studio-rules">
      {links.map(([t, d, to]) => (
        <button key={to} onClick={() => nav(to)} data-testid={`studio-rule-link-${to.split("/").pop()}`}
          className="text-left rounded-xl border border-slate-200 bg-white p-4 hover:border-indigo-300 hover:shadow-sm transition-all">
          <div className="flex items-center justify-between">
            <span className="font-medium text-slate-900 text-[14px]">{t}</span>
            <ArrowUpRight className="h-4 w-4 text-slate-400" />
          </div>
          <p className="text-[12px] text-slate-500 mt-2">{d}</p>
        </button>
      ))}
    </div>
  );
}
